/* eslint-disable eqeqeq */
import { useTranslate } from '@pankod/refine-core'

import {
  Alert,
  Checkbox,
  CreateButton,
  DateField,
  FilterDropdown,
  FilterDropdownProps,
  Icons,
  Input,
  List,
  Radio,
  ShowButton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  useDrawerForm,
  useModal
} from '@pankod/refine-antd'

import { AdviceModal } from 'components/AdviceModal/AdviceModal'
import { ExportToExcelButton } from 'components/ExportToExcelButton/ExportToExcelButton'
import { DateFilterDropdown, QuantityFilterDropdown, SelectFilterDropdown } from 'components/filterDropdown'
import { Order } from 'interfaces'
import { CreateOrder } from 'pages/orders/create'
import { Key, useEffect, useRef, useState } from 'react'
import { currency, filter, getOrderExport, getOrders, sorter, supabaseClient } from 'utility'
import { currentUserEmail } from 'utility/authLocal'
import { AMAZON_MARKETPLACE_ID, CUSTOM_ORDER_MARKETPLACE_ID, DEFAULT_DATE_TIME_FORMAT, SHIPPING_METHOD_ECONOMY_ID, SHIPPING_METHOD_REDUCED_ID } from 'utility/constants'
import '../../custom.css'
import { RequestOrdersListProps } from './list'
import { readManualReview, readUnpurchasedManualReview, ManualReviewRow } from './manualReview'
import { getPurchaseUrl } from './specialStores'
import { requestedOrderSelection, requestedTabMembership } from './requestedTabMembership'
import { executableSearchFilters, ExecutableSearch } from './executableSearch'
import { getPurchasableOrderGroups } from './purchasableSpecialOrders'
import { getColorLabel } from 'utility/colorLabel'
import { capitalizeFirstLetter } from 'utility/capitalizeFirstLetter'

type CheckboxWithAdviseProps = {
  record: any
}

export const CheckboxWithAdvise: React.FC<CheckboxWithAdviseProps> = ({ record }) => {
  const t = useTranslate()
  const { modalProps, show, close } = useModal()

  const [recordCompleted, setRecordCompleted] = useState(record.completed)

  const handleCompletedChange = async (value: any, record: any) => {
    if (record.id) {
      await supabaseClient.from('orders')
        .update({
          completed: value,
          updated_by: supabaseClient.auth.user()?.email
        })
        .eq('id', record.id)

      setRecordCompleted(value)
    }
  }
  return (
    <>
      <Checkbox checked={recordCompleted} onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        show()
      }} onChange={() => null} />
      <AdviceModal
        infoText={(!recordCompleted) ? t('order.actions.adviseToMarkAsCompleted', { order_id: record.id }) : t('order.actions.adviseToMarkAsIncompleted', { order_id: record.id })}
        okFunction={() => handleCompletedChange(!recordCompleted, record)}
        close={close}
        show={show}
        modalProps={modalProps} />
    </>
  )
}
export type tablePropsProps = {
  dataSource: any[]
}

export const RequestOrdersList: React.FC<RequestOrdersListProps> = ({ active = true, select, dataToExport, getConditionalClassName, onlyPaidOrders, specialFilter }) => {
  const manual = specialFilter === 'manualReview'
  const [textSearch, setTextSearch] = useState<ExecutableSearch>({ url: '', reason: '', notes: '' })
  const [filterStatusId, setFilterStatusId] = useState<Key[]>([])
  const [reviewRows, setReviewRows] = useState<ManualReviewRow[]>([])
  const requestSequence = useRef(0)
  let callingServer = false
  const prepareInitialFilter = () => {
    if (manual || specialFilter === 'specials') return [{ field: 'supplements.deleted', operator: 'eq', value: false }] as filter[]
    const economyReduced: number[] = [SHIPPING_METHOD_ECONOMY_ID, SHIPPING_METHOD_REDUCED_ID]
    const filt = [{ field: 'supplements.deleted', operator: 'eq', value: false }]

    if (specialFilter === 'economyReduced') {
      filt.push({ field: 'shipping_method_id', operator: 'in', value: economyReduced } as filter)
      // economy orders can't have a subscription: keep reduced orders or non-subscribed economy orders
      filt.push({ field: '', operator: 'or', value: `shipping_method_id.neq.${SHIPPING_METHOD_ECONOMY_ID}, subscribed_order.is.null` } as filter)
    } else if (specialFilter === 'requestedSuscription') {
      // exclude reduced orders and show only economy with subscription
      filt.push({ field: 'shipping_method_id', operator: 'eq', value: SHIPPING_METHOD_ECONOMY_ID } as filter)
      filt.push({ field: 'subscribed_order', operator: 'not', value: null } as filter)
    } else if (specialFilter === 'amazon') {
      filt.push({ field: 'marketplace_id.id', operator: 'eq', value: AMAZON_MARKETPLACE_ID } as filter)
      // exclude economyReduced
      filt.push({ field: 'shipping_method_id', operator: 'notin', value: economyReduced } as filter)
    } else if (specialFilter === 'mktPlace') {
      filt.push({ field: 'items.seller', operator: 'not', value: null } as filter)
      filt.push({ field: 'marketplace_id.is_marketplace', operator: 'eq', value: true } as filter)
      // exclude economyReduced and amazon
      filt.push({ field: 'shipping_method_id', operator: 'notin', value: economyReduced } as filter)
      filt.push({ field: 'marketplace_id.id', operator: 'neq', value: AMAZON_MARKETPLACE_ID } as filter)
    } else if (specialFilter === 'specials') {
      // Classified by order ID below, including custom purchase URLs.
    } else if (specialFilter === 'intern') {
      filt.push({ field: 'marketplace_id', operator: 'neq', value: CUSTOM_ORDER_MARKETPLACE_ID } as filter)
      // exclude economyReduced, amazon and mktPlace
      filt.push({ field: 'shipping_method_id', operator: 'notin', value: economyReduced } as filter)
      filt.push({ field: 'marketplace_id.id', operator: 'neq', value: AMAZON_MARKETPLACE_ID } as filter)
      filt.push({ field: 'items.seller', operator: 'is', value: null } as filter)
      filt.push({ field: 'marketplace_id.is_marketplace', operator: 'neq', value: true } as filter)
    } else if (specialFilter === 'custom') {
      filt.push({ field: 'marketplace_id', operator: 'eq', value: CUSTOM_ORDER_MARKETPLACE_ID } as filter)
      // exclude economyReduced, amazon and mktPlace
      filt.push({ field: 'shipping_method_id', operator: 'notin', value: economyReduced } as filter)
      filt.push({ field: 'marketplace_id.id', operator: 'neq', value: AMAZON_MARKETPLACE_ID } as filter)
      filt.push({ field: 'items.seller', operator: 'is', value: null } as filter)
      filt.push({ field: 'marketplace_id.is_marketplace', operator: 'neq', value: true } as filter)
    }

    return filt
  }

  const searchDropdown = (field: 'url' | 'reason', props: FilterDropdownProps) => (
    <FilterDropdown {...props}
      confirm={() => {
        setTextSearch(previous => ({ ...previous, [field]: String(props.selectedKeys[0] || '') }))
        setAdditionalFilter(true)
        props.confirm()
      }}
      clearFilters={() => {
        setTextSearch(previous => ({ ...previous, [field]: '' }))
        setAdditionalFilter(true)
        props.setSelectedKeys([])
        props.clearFilters?.()
      }}>
      <Input placeholder='Buscar texto' value={String(props.selectedKeys[0] || '')}
        onChange={event => props.setSelectedKeys(event.target.value ? [event.target.value] : [])} />
    </FilterDropdown>
  )

  const [tableProps, setTableProps] = useState<tablePropsProps>({ dataSource: [] })
  const [filters, setFilters] = useState<filter[]>(prepareInitialFilter())
  const [sorter, setSorter] = useState<sorter>({ field: 'id', ascending: false })

  const getOrdersList = async (resetPages: boolean = false) => {
    if (!active) return
    const sequence = ++requestSequence.current
    if (!callingServer) {
      setLoading(true)
      let cPage = (resetPages) ? 1 : currentPage
      let cPageSize = pageSize
      callingServer = true
      setLoadError(false)
      try {
        const executable = manual ? { ids: [], specialIds: [] } : await getPurchasableOrderGroups(supabaseClient)

        if (specialFilter === 'economyReduced') {
          cPage = 1
          cPageSize = 10000
        }

        const queue = await (manual ? readUnpurchasedManualReview(supabaseClient) : readManualReview(supabaseClient))
        const searchFilters = await executableSearchFilters(supabaseClient, manual ? queue.map(row => row.order_id) : executable.ids, queue, textSearch)
        let { data: dataSource, count } = await getOrders(requestedOrderSelection(select, specialFilter), cPage, cPageSize, [...prepareInitialFilter(), ...filters, ...requestedTabMembership(specialFilter, executable.specialIds, queue, executable.ids), ...searchFilters], sorter)
        if (sequence !== requestSequence.current) return
        setReviewRows(queue)

        if (specialFilter === 'economyReduced') {
          dataSource = dataSource.filter((order: Order) => {
            if ((order.shipping_method_id as any)?.id && (order.shipping_method_id as any)?.id !== SHIPPING_METHOD_ECONOMY_ID) return true
            else {
              if (order.subscribed_order === null) return true
              else {
                if (order.syniva_price >= 0 && order.syniva_price < 40 && order.subscribed_order === null) return true
                return false
              }
            }
          })
          count = dataSource.length
        }

        const tableProps = {
          dataSource: dataSource
        }
        tableProps.dataSource = (tableProps.dataSource as any)?.map((row: any) => {
          row.action_dates = { cancelation_date: row.cancelation_date, return_date: row.return_date }
          return row
        })

        if (onlyPaidOrders) {
          tableProps.dataSource = tableProps.dataSource?.filter((x: any) => x.status_id && x.status_id > 0)
        }
        setTableProps(tableProps)
        setTotalData(count || 0)
      } catch {
        if (sequence !== requestSequence.current) return
        setLoadError(true)
        setTableProps({ dataSource: [] })
        setTotalData(0)
      } finally {
        if (sequence === requestSequence.current) setLoading(false)
      }
    }
    callingServer = false
  }

  const [loadError, setLoadError] = useState(false)
  const [totalData, setTotalData] = useState<number>(0)
  const [currentPage, setCurrentPage] = useState(localStorage.getItem('requestOrdersPage') ? parseInt(localStorage.getItem('requestOrdersPage') || '1') : 1)
  const [pageSize, setPageSize] = useState(localStorage.getItem('requestOrdersPageSize') ? parseInt(localStorage.getItem('requestOrdersPageSize') || '10') : 10)
  const [loading, setLoading] = useState(false)
  const [additionalFilter, setAdditionalFilter] = useState<boolean>(false)

  // filter values
  const [filterId, setFilterId] = useState<number|undefined>(undefined)
  const [filteredIdState, setFilteredIdState] = useState<boolean>(false)
  const [filterAlias, setFilterAlias] = useState<string|undefined>(undefined)
  const [filteredAliasState, setFilteredAliasState] = useState<boolean>(false)
  const [filterMarketplaceId, setFilterMarketplaceId] = useState<Key[]|undefined>(undefined)
  const [filteredMarketplaceIdState, setFilteredMarketplaceIdState] = useState<boolean>(false)
  const [filteredShippingMethodIdState, setFilteredShippingMethodIdState] = useState<boolean>(false)
  const [filterShippingMethodId, setFilterShippingMethodId] = useState<Key[]|undefined>(undefined)
  const [filterPriority, setFilterPriority] = useState<boolean|undefined>(undefined)

  const [filteredCreatedAtState, setFilteredCreatedAtState] = useState<boolean>(false)
  const [filteredTotalState, setFilteredTotalState] = useState<boolean>(false)
  const [filteredPriorityState, setFilteredPriorityState] = useState<boolean>(false)

  const t = useTranslate()

  useEffect(() => {
    // console.log('filters -->', filters, additionalFilter)
    if (!callingServer) {
      getOrdersList(additionalFilter)
    }
  }, [filters, active, textSearch])

  useEffect(() => {
    // console.log('sorter -->', sorter)
    if (!callingServer) {
      if (tableProps.dataSource.length > 0) {
        getOrdersList()
      }
    }
  }, [sorter, currentPage, pageSize])

  useEffect(() => {
    if (!active) return
    const refresh = () => { if (!document.hidden) getOrdersList() }
    const timer = window.setInterval(refresh, 60000)
    window.addEventListener('focus', refresh)
    return () => { requestSequence.current++; window.clearInterval(timer); window.removeEventListener('focus', refresh) }
  }, [active, filters, sorter, currentPage, pageSize])

  const { Text } = Typography

  const {
    drawerProps: createDrawerProps,
    formProps: createFormProps,
    saveButtonProps: createSaveButtonProps,
    show: createShow,
    close: createClose
  } = useDrawerForm<Order>({
    action: 'create',
    resource: 'orders',
    redirect: false
  })

  const fixedMobile = window.innerWidth > 700

  async function exportOrders () {
    const selection = requestedOrderSelection(select, specialFilter)
    const executable = manual ? { ids: [], specialIds: [] } : await getPurchasableOrderGroups(supabaseClient)

    const queue = await (manual ? readUnpurchasedManualReview(supabaseClient) : readManualReview(supabaseClient))
    const searchFilters = await executableSearchFilters(supabaseClient, manual ? queue.map(row => row.order_id) : executable.ids, queue, textSearch)
    let data = await getOrderExport(selection, [...prepareInitialFilter(), ...filters, ...requestedTabMembership(specialFilter, executable.specialIds, queue, executable.ids), ...searchFilters], sorter)

    if (onlyPaidOrders) {
      data = data?.filter((x: any) => x.status_id && x.status_id > 0)
    }

    const exported = dataToExport(data as any[])
    return manual
      ? exported.map((row: any, index: number) => {
        const review = queue.find(entry => entry.order_id === data[index].id)
        return { ...row, 'Motivo de revisión': review?.reason || '', 'Artículos afectados': review?.affected_item_ids.join(', ') || '' }
      })
      : exported
  }

  return (
    <>
      {loadError && <Alert type='error' showIcon message='No se han podido clasificar los pedidos. Vuelve a cargar la página.' />}
      <List
        resource='orders'
        title={
          <>
            {t('order.orders')}
            <Text type='secondary' style={{ fontSize: 10, display: 'block', lineHeight: 2 }}>{t('order.cancelHelper')}</Text>
            <Text type='secondary' style={{ fontSize: 10, display: 'block', lineHeight: 2 }}>{t('order.returnHelper')}</Text>
          </>
        }
        headerProps={{
          extra: <>
            {!manual && <CreateButton onClick={() => createShow()}>{t('order.actions.addOrder')}</CreateButton>}
            <Input.Search placeholder='Buscar en notas internas' allowClear style={{ width: 230 }}
              onSearch={notes => { setTextSearch(previous => ({ ...previous, notes })); setAdditionalFilter(true) }} />
            <ExportToExcelButton filename='Pedidos' asyncFunction={() => exportOrders()} />
          </>
        }}
      >
        <Table
          {...tableProps}
          rowKey="id"
          rowClassName={(record) => getConditionalClassName(record)}
          loading={loading}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: totalData,
            onChange (page, pageSize) {
              setCurrentPage(page)
              setPageSize(pageSize)
              localStorage.setItem('requestOrdersPage', page.toString() || '')
              localStorage.setItem('requestOrdersPageSize', pageSize.toString() || '')
            }
          }}
          onChange={(_pagination: any, _filters: any, sorter: any, _extra: any) => {
            if (sorter && sorter.order) {
              setSorter({ field: sorter.field, ascending: (sorter.order === 'ascend') })
            } else {
              setSorter({ field: 'id', ascending: false })
            }
          }}
          scroll={{ x: manual ? 1300 : specialFilter === 'economyReduced' ? 1100 : 1000, y: '60vh' }}
          >
          <Table.Column
            key='id'
            fixed = {fixedMobile ? 'left' : false}
            dataIndex="id"
            align="center"
            title={t('order.fields.id')}
            render={(value, record: Order) => (
              <>
                {value}
                {record.comments?.trim() && (
                  <div>
                    <Tooltip title={<span style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{record.comments}</span>} placement='bottom'>
                      <Tag color='red' style={{ marginRight: 0 }} tabIndex={0}>
                        Notas internas
                      </Tag>
                    </Tooltip>
                  </div>
                )}
              </>
            )}
            filtered={filteredIdState}
            filterDropdown={(props) => (
              <FilterDropdown {...props}
                mapValue={(selectedKeys) => {
                  setFilterId(Number(selectedKeys))
                  return selectedKeys
                }}
                clearFilters={() => {
                  setFilters([...filters.filter((x: filter) => x.field !== 'id')])
                  setFilterId(undefined)
                  setFilteredIdState(false)
                  setAdditionalFilter(false)
                }}
                confirm={() => {
                  if (filterId) {
                    if (filters.find((x: filter) => x.field === 'id')) {
                      const updatedFilters = [...filters]
                      const index = updatedFilters.findIndex((x: filter) => x.field === 'id')
                      updatedFilters[index].value = filterId

                      setFilters([...updatedFilters])
                    } else {
                      setFilters([...filters, ...[{ field: 'id', operator: 'eq', value: filterId }]])
                    }
                    setFilteredIdState(true)
                    setAdditionalFilter(true)
                  }
                }}
              >
                <Input type='text' />
              </FilterDropdown>
            )}
            sorter
            width={120}
          />
          <Table.Column
            key='alias'
            fixed = {fixedMobile ? 'left' : false}
            dataIndex="alias"
            align="center"
            title={t('order.fields.alias')}
            sorter
            width={120}
            filtered={filteredAliasState}
            filterDropdown={(props) => (
              <FilterDropdown {...props}
                mapValue={(selectedKeys) => {
                  setFilterAlias(selectedKeys.toString().toUpperCase())
                  return selectedKeys.toString().toUpperCase()
                }}
                clearFilters={() => {
                  setFilters([...filters.filter((x: filter) => x.field !== 'alias')])
                  setFilterId(undefined)
                  setFilteredAliasState(false)
                  setAdditionalFilter(false)
                }}
                confirm={() => {
                  if (filterAlias) {
                    if (filters.find((x: filter) => x.field === 'alias')) {
                      const updatedFilters = [...filters]
                      const index = updatedFilters.findIndex((x: filter) => x.field === 'alias')
                      updatedFilters[index].value = filterAlias

                      setFilters([...updatedFilters])
                    } else {
                      setFilters([...filters, ...[{ field: 'alias', operator: 'eq', value: filterAlias }]])
                    }
                    setFilteredAliasState(true)
                    setAdditionalFilter(true)
                  }
                }}
              >
                <Input type='text' />
              </FilterDropdown>
            )}
          />
          <Table.Column
            key="is_priority"
            dataIndex="is_priority"
            align="center"
            title={<Tooltip title={t('order.fields.is_priority')}><span aria-label={t('order.fields.is_priority')}>!</span></Tooltip>}
            sorter
            width={70}
            render={(value) => (
              <Space style={{ justifyContent: 'center' }}>
                {(value) ? <Icons.ExclamationCircleFilled style={{ fontSize: 20, color: '#f16868' }} /> : null}
              </Space>
            )}
            filtered={filteredPriorityState}
            filterDropdown={(props: FilterDropdownProps) => (
              <FilterDropdown {...props}
                mapValue={(selectedKeys) => {
                  setFilterPriority(selectedKeys.toString() === 'true')
                  return selectedKeys.toString() === 'true'
                }}
                clearFilters={() => {
                  setFilters([...filters.filter((x: filter) => x.field !== 'is_priority')])
                  setFilterPriority(undefined)
                  setFilteredPriorityState(false)
                  setAdditionalFilter(false)
                }}
                confirm={() => {
                  console.log(filterPriority)
                  if (typeof filterPriority === 'boolean') {
                    if (filters.find((x: filter) => x.field === 'is_priority')) {
                      const updatedFilters = [...filters]
                      const index = updatedFilters.findIndex((x: filter) => x.field === 'is_priority')
                      updatedFilters[index].value = filterPriority

                      setFilters([...updatedFilters])
                    } else {
                      setFilters([...filters, ...[{ field: 'is_priority', operator: 'eq', value: filterPriority }]])
                    }
                    setFilteredPriorityState(true)
                    setAdditionalFilter(true)
                  }
                }}
              >
                <Radio.Group>
                  <Radio value={true}>{t('order.fields.priority')}</Radio>
                  <Radio value={false}>{t('order.fields.noPriority')}</Radio>
                </Radio.Group>
              </FilterDropdown>
            )}
          />

          <Table.Column
            key='status_id'
            filtered={filters.some(filter => filter.field === 'status_id')}
            filterDropdown={props => {
              props.setSelectedKeys = setFilterStatusId
              props.clearFilters = () => {
                setFilters(previous => previous.filter(filter => filter.field !== 'status_id'))
                setFilterStatusId([])
                setAdditionalFilter(true)
              }
              props.confirm = () => {
                setFilters(previous => [...previous.filter(filter => filter.field !== 'status_id'),
                  ...(filterStatusId.length ? [{ field: 'status_id', operator: 'in', value: filterStatusId }] as filter[] : [])])
                setAdditionalFilter(true)
              }
              return <SelectFilterDropdown props={props} entity='status' optionLabel='name' optionValue='id' />
            }}
            dataIndex={['status', 'name']}
            title={t('order.fields.status')}
            width={120}
            render={(value, record: Order) => (
              <Tag color={getColorLabel(value, record.prev_status_id)}>{capitalizeFirstLetter(value || '')}</Tag>
            )}
          />

          <Table.Column
            key='created_at'
            dataIndex="created_at"
            align="center"
            title={t('order.fields.createdAt')}
            render={(value) => <DateField format={DEFAULT_DATE_TIME_FORMAT} value={value}/>}
            sorter
            width={120}
            filtered={filteredCreatedAtState}
            filterDropdown={() => (
              <DateFilterDropdown
                field='created_at'
                filters={filters}
                setFilters={(newFilter: any, method: string) => {
                  if (newFilter.length > 0 && method === 'merge') {
                    let filtersToAdd = [...filters]

                    if (filters.find((x: filter) => x.field === 'created_at' && x.operator === 'gte')) {
                      const updatedFilters = [...filtersToAdd]
                      const index = updatedFilters.findIndex((x: filter) => x.field === 'created_at' && x.operator === 'gte')
                      updatedFilters[index].value = newFilter[0].value

                      filtersToAdd = [...updatedFilters]
                    } else {
                      filtersToAdd = [...filtersToAdd, ...[newFilter[0]]]
                    }

                    if (filters.find((x: filter) => x.field === 'created_at' && x.operator === 'lte')) {
                      const updatedFilters = [...filtersToAdd]
                      const index = updatedFilters.findIndex((x: filter) => x.field === 'created_at' && x.operator === 'lte')
                      updatedFilters[index].value = newFilter[1].value

                      filtersToAdd = [...updatedFilters]
                    } else {
                      filtersToAdd = [...filtersToAdd, ...[newFilter[1]]]
                    }

                    setFilters([...filtersToAdd])
                    setFilteredCreatedAtState(true)
                    setAdditionalFilter(true)
                  } else {
                    setFilters([...filters.filter((x: filter) => x.field !== 'created_at')])
                    setFilteredCreatedAtState(false)
                    setAdditionalFilter(false)
                  }
                }}
              />
            )}
          />
          <Table.Column
            dataIndex={['marketplace_id', 'name']}
            align="center"
            title={t('order.fields.marketplace')}
            sorter
            width={120}
            key="marketplace_id"
            filtered={filteredMarketplaceIdState}
            filterDropdown={(props: FilterDropdownProps) => {
              props.setSelectedKeys = setFilterMarketplaceId
              props.clearFilters = () => {
                setFilters([...filters.filter((x: filter) => x.field !== 'marketplace_id')])
                setFilterMarketplaceId(undefined)
                setFilteredMarketplaceIdState(false)
                setAdditionalFilter(false)
              }
              props.confirm = () => {
                if (filterMarketplaceId && filterMarketplaceId.length > 0) {
                  if (filters.find((x: filter) => x.field === 'marketplace_id')) {
                    const updatedFilters = [...filters]
                    const index = updatedFilters.findIndex((x: filter) => x.field === 'marketplace_id')
                    updatedFilters[index].value = filterMarketplaceId

                    setFilters([...updatedFilters])
                  } else {
                    setFilters([...filters, ...[{ field: 'marketplace_id', operator: 'in', value: filterMarketplaceId }]])
                  }
                  setFilteredMarketplaceIdState(true)
                  setAdditionalFilter(true)
                }
              }
              return <SelectFilterDropdown
                props={props}
                entity='marketplaces'
                optionLabel='name'
                optionValue='id'
                filters={[{
                  field: 'deleted',
                  operator: 'eq',
                  value: false
                }]}
              />
            }}
          />
          <Table.Column
            key='purchase_url'
            filtered={!!textSearch.url.trim()}
            filterDropdown={props => searchDropdown('url', props)}
            title={t('order.fields.purchaseUrl')}
            width={160}
            ellipsis
            render={(_value, record: Order) => getPurchaseUrl(record)}
          />
          <Table.Column
            key='total'
            dataIndex="total"
            align="center"
            title={t('order.fields.billingAmount')}
            sorter
            width={120}
            render={(value: number) => {
              return currency(value)
            }}
            filtered={filteredTotalState}
            filterDropdown={() => (
              <QuantityFilterDropdown
                field='total'
                filters={filters}
                setFilters={(newFilter: any) => {
                  if (Array.isArray(newFilter)) {
                    setFilters([...newFilter])
                    setFilteredTotalState(true)
                    setAdditionalFilter(true)
                  } else {
                    setFilters([...filters.filter((x: filter) => x.field !== 'total')])
                    setFilteredTotalState(false)
                    setAdditionalFilter(false)
                  }
                }}
              />
            )}
          />
          {specialFilter === 'economyReduced' && (
            <Table.Column
              key='shipping_method_id'
              dataIndex={['shipping_method_id', 'name']}
              align="center"
              title={t('order.fields.shipmentMethod')}
              sorter
              width={120}
              filtered={filteredShippingMethodIdState}
              filterDropdown={(props: FilterDropdownProps) => {
                props.setSelectedKeys = setFilterShippingMethodId
                props.clearFilters = () => {
                  setFilters([...filters.filter((x: filter) => x.field !== 'shipping_method_id')])
                  setFilterShippingMethodId(undefined)
                  setFilteredShippingMethodIdState(false)
                }
                props.confirm = () => {
                  if (filterShippingMethodId && filterShippingMethodId.length > 0) {
                    if (filters.find((x: filter) => x.field === 'shipping_method_id')) {
                      const updatedFilters = [...filters]
                      const index = updatedFilters.findIndex((x: filter) => x.field === 'shipping_method_id')
                      updatedFilters[index].value = filterShippingMethodId

                      setFilters([...updatedFilters])
                    } else {
                      setFilters([...filters, ...[{ field: 'shipping_method_id', operator: 'in', value: filterShippingMethodId }]])
                    }
                    setFilteredShippingMethodIdState(true)
                  }
                }
                return <SelectFilterDropdown
                  props={props}
                  entity='shipment_methods'
                  optionLabel='name'
                  optionValue='id'
                  filters={[{
                    field: 'deleted',
                    operator: 'eq',
                    value: false
                  }]}
                />
              }}
            />
          )}
          {manual && <Table.Column key='manual_review_reason' title='Motivo de revisión'
            filtered={!!textSearch.reason.trim()}
            filterDropdown={props => searchDropdown('reason', props)}
            width={320}
            render={(_value, record: Order) => {
              const review = reviewRows.find(row => row.order_id === record.id)
              return <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {review?.affected_item_ids.length ? <div>Artículos: {review.affected_item_ids.join(', ')}</div> : null}
                {review?.reason}
              </div>
            }} />}
          <Table.Column<Order>
            fixed="right"
            title={t('table.actions')}
            dataIndex="actions"
            key="actions"
            align="center"
            width={100}
            // width= {20}
            render={(_, record) => {
              if (record.locked) {
                return (
                  <>
                    <Icons.LockOutlined style={{ color: '#f5222d', fontSize: 20 }} />
                    {record.locked_by === currentUserEmail() && (
                      <ShowButton
                        resourceNameOrRouteName='orders'
                        hideText
                        style={{ color: 'green', fontSize: '15px', marginLeft: 5 }}
                        recordItemId={record.id}
                      />
                    )}
                    <p>{record?.locked_by}</p>
                  </>
                )
              }
              return (
                <ShowButton
                  resourceNameOrRouteName='orders'
                  hideText
                  style={{ color: 'green', fontSize: '15px' }}
                  recordItemId={record.id}
                />
              )
            }}
          />
        </Table>
        <CreateOrder
          drawerProps={createDrawerProps}
          formProps={createFormProps}
          saveButtonProps={createSaveButtonProps}
          closeDrawer={createClose}
        />
      </List>
    </>
  )
}
